import { Typography, Box, Chip } from "@mui/material";
import { map } from "lodash";
import { Moment } from "moment";
import PosicaoAtivoInternacional from "../../../models/posicaoAtivoInternacional";
import TabelaBase, { CelulaValor, CelulaNumero } from "./tabelaBase";
import MoedaText from "../ui/inputs/moedaText";
import { escalaQuantidade } from "../ui/inputs/quantidade";
import {
  totalCustodiaBRL,
  totaisCustodiaPorMoeda,
} from "../../app/internacional/totaisCustodia";
import {
  MoedaInternacional,
  TipoOperacaoInternacional,
} from "../../../models/enum";

// Valor na moeda de origem, com o código ISO como prefixo (ex.: "USD 75,99").
// Mesma formatação pt-BR do MoedaText para o número ficar alinhado com a
// coluna em BRL ao lado.
function CelulaValorMoeda({
  valor,
  moeda,
}: {
  valor: number;
  moeda: MoedaInternacional;
}) {
  return (
    <Typography variant="dataMono" noWrap>
      <MoedaText value={valor} prefix={`${moeda} `} />
    </Typography>
  );
}

type Props = {
  posicoes: PosicaoAtivoInternacional[];
  loading?: boolean;
  dataReferencia?: Moment;
};

export default function TabelaCustodiaInternacional({
  posicoes,
  loading = false,
  dataReferencia,
}: Props) {
  const header = [
    { titulo: "Ativo", key: "ativo", ordem: 1, align: "left" as const },
    { titulo: "Moeda", key: "moeda", ordem: 2, align: "center" as const },
    {
      titulo: "Quantidade",
      key: "quantidade",
      ordem: 3,
      align: "right" as const,
    },
    // Custo médio e valor na moeda de origem + em BRL: o contador confere o
    // PM contra o statement e o custo em reais é a base do ganho de capital
    // (feedback da Ana, set/2026).
    {
      titulo: "PM (moeda)",
      key: "custoMedioOriginal",
      ordem: 4,
      align: "right" as const,
    },
    {
      titulo: "Valor (moeda)",
      key: "valorOriginal",
      ordem: 5,
      align: "right" as const,
    },
    {
      titulo: "PM (BRL)",
      key: "custoMedioBRL",
      ordem: 6,
      align: "right" as const,
    },
    { titulo: "Valor (BRL)", key: "valor", ordem: 7, align: "right" as const },
  ];

  // Posição vendida (opção lançada) é obrigação: quantidade negativa, chip
  // "lançada" e o valor mostrado é o prêmio recebido — fora do total.
  // Bens de fato (compradas) primeiro; lançadas no fim. Entre iguais, mantém
  // a ordem do cálculo.
  const ordenadas = [
    ...posicoes.filter(
      (p) => p.tipoOperacao !== TipoOperacaoInternacional.SELL
    ),
    ...posicoes.filter(
      (p) => p.tipoOperacao === TipoOperacaoInternacional.SELL
    ),
  ];
  const totais = totaisCustodiaPorMoeda(posicoes);
  const totalBRL = totalCustodiaBRL(posicoes);
  const rows = map(ordenadas, (posicao) => {
    const lancada = posicao.tipoOperacao === TipoOperacaoInternacional.SELL;
    const sinal = lancada ? -1 : 1;
    return {
      ativo: (
        <Box display="flex" alignItems="center" gap={0.75}>
          <Typography variant="dataMono" fontWeight={500}>
            {posicao.codigoAtivo}
          </Typography>
          {lancada && (
            <Chip
              label="lançada"
              size="small"
              sx={(theme) => ({
                backgroundColor: theme.palette.statusChip.warn.bg,
                color: theme.palette.statusChip.warn.fg,
              })}
            />
          )}
        </Box>
      ),
      // Moeda é rótulo, não status: chip neutro do sistema.
      moeda: (
        <Chip
          label={posicao.moeda}
          size="small"
          sx={(theme) => ({
            backgroundColor: theme.palette.statusChip.neutral.bg,
            color: theme.palette.statusChip.neutral.fg,
            minWidth: 50,
          })}
        />
      ),
      quantidade: (
        <CelulaNumero
          numero={sinal * posicao.quantidade}
          escala={escalaQuantidade(posicao.quantidade)}
        />
      ),
      custoMedioOriginal: (
        <CelulaValorMoeda
          valor={posicao.custoMedioOriginal}
          moeda={posicao.moeda}
        />
      ),
      valorOriginal: (
        <CelulaValorMoeda
          valor={posicao.valorTotalOriginal}
          moeda={posicao.moeda}
        />
      ),
      custoMedioBRL: <CelulaValor valor={posicao.custoMedioBRL} />,
      valor: <CelulaValor valor={posicao.valorTotalBRL} />,
    };
  });

  return (
    <Box>
      <TabelaBase header={header} rows={rows} loading={loading} />

      {!loading && totais.length > 0 && (
        // Total pelo custo de aquisição, sem as lançadas: em reais (PTAX de
        // cada compra) e na moeda de origem (sem conversão).
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="baseline"
          flexWrap="wrap"
          columnGap={3}
          rowGap={0.5}
          mt={1.5}
          px={1}
        >
          <Typography variant="labelMono" color="text.secondary">
            Total (custo de aquisição)
          </Typography>
          {totais.map((t) => (
            <Typography key={t.moeda} variant="dataMono" noWrap>
              <MoedaText value={t.valorOriginal} prefix={`${t.moeda} `} />
            </Typography>
          ))}
          <Typography variant="dataMono" noWrap sx={{ fontWeight: 500 }}>
            <MoedaText value={totalBRL} />
          </Typography>
        </Box>
      )}

      {dataReferencia && (
        <Box mt={1} textAlign="right">
          <Typography variant="labelMono" color="text.secondary">
            em {dataReferencia.format("DD/MM/YY")}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
